// What a war sounds like, period by period: five sound packages that follow the weapons of their time, as the
// pictures do (logic.ts eras). For each event the director plays, a short score of cues: which sounds, when, how
// loud, where around the event. No gunpowder before 1420; no machine guns before the Gatling; no aircraft before
// 1914. Atrocities only ever get a distant bell. Pure functions (tested in score.test.ts).

import type { SampleId } from "./foley";
import {
  ERA_BLADE, ERA_MODERN, ERA_POWDER, ERA_RIFLE, ERA_SHELL, eraOf, FX_BATTLE, FX_BOMBING, FX_MEMORIAL, FX_NAVAL, FX_NONE,
  FX_NUKE, FX_SIEGE, FX_UPRISING,
} from "./logic";
import type { Rng } from "./dsp";

/** Sounds made on the spot (Web Audio), rather than from a rendered sample. */
export type LiveId = "boom" | "whistle" | "siren" | "stuka" | "planes" | "jet" | "drone" | "bell";
export type SoundId = SampleId | LiveId;
export const LIVE = new Set<SoundId>(["boom", "whistle", "siren", "stuka", "planes", "jet", "drone", "bell"]);

export interface Cue {
  s: SoundId;
  at: number; // seconds after the event's sound arrives
  g: number; // level, about 0–1
  rate?: number; // playback rate: pitch and pace (samples)
  pan?: number; // left/right of the event (−1…1)
  far?: number; // further off than the event (0–1): quieter, duller, more echo
  len?: number; // seconds, for loops and live sounds
  n?: number; // repeated: a burst, a volley, a drum roll
  every?: number; // …this far apart (s), a little unevenly
  within?: number; // …or at random moments within this many seconds
  sweep?: number; // passes overhead: travels this far across, left to right
}

export interface Pack { era: number; name: string; years: string; heard: string; hear: number } // hear: a year to preview

export const PACKS: Pack[] = [
  { era: ERA_BLADE, name: "Swords and arrows", years: "before 1420", hear: 1410,
    heard: "war horns, battle cries, clashing steel, arrow volleys, cavalry, siege engines, oared ships" },
  { era: ERA_POWDER, name: "Muskets and cannon", years: "1420–1860", hear: 1805,
    heard: "drums, musket volleys, cannon, broadsides, cavalry charges, bugles from the 1750s" },
  { era: ERA_RIFLE, name: "Rifles and the first machine guns", years: "1860–1912", hear: 1890,
    heard: "rifle fire, Gatling and Maxim guns, shells, bugle calls" },
  { era: ERA_SHELL, name: "The world wars", years: "1912–1960", hear: 1943,
    heard: "artillery barrages, machine guns, tanks, aircraft, air-raid sirens, falling bombs" },
  { era: ERA_MODERN, name: "Modern war", years: "since 1960", hear: 2023,
    heard: "assault rifles, heavy machine guns, rockets, helicopters, jets, drones from the 2010s" },
];

export const packOf = (t: number): Pack => PACKS[eraOf(t)];

/** The first and last years each sound belongs to (checked by the tests against every score). */
export const PERIOD: Partial<Record<SoundId, [number, number]>> = {
  horn: [-3000, 1600], wardrum: [-3000, 1700], bow: [-3000, 1600], arrow: [-3000, 1600], thud: [-3000, 1600],
  treb: [-3000, 1500], ram: [-3000, 1600], oars: [-3000, 1700],
  musket: [1420, 1900], cannon: [1420, 9999], boom: [1420, 9999], snare: [1500, 1960], bugle: [1750, 1920],
  rifle: [1860, 9999], blast: [1860, 9999], whistle: [1860, 9999], mg: [1862, 1960],
  planes: [1914, 1960], siren: [1914, 9999], tank: [1916, 9999], hmg: [1918, 9999], stuka: [1937, 1946], rocket: [1941, 9999],
  ar: [1960, 9999], jet: [1950, 9999], rotor: [1962, 9999], drone: [2014, 9999], sword: [-3000, 1920], shield: [-3000, 1700],
};

const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));
const span = (r: Rng, a: number, b: number) => a + (b - a) * r();
const side = (r: Rng, s: number) => s * span(r, 0.15, 0.7); // one army on the left, the other on the right

// --- building blocks ------------------------------------------------------------------------------------------

/** Arrows: bows released together, the volley hissing over, striking shields and the ground. */
function arrows(c: Cue[], r: Rng, at: number, m: number) {
  const n = 4 + Math.round(16 * m);
  c.push({ s: "bow", at, g: 0.4, n: Math.min(8, n), within: 0.3, pan: -0.35 });
  c.push({ s: "arrow", at: at + 0.15, g: 0.5, n, within: 0.55, pan: span(r, -0.2, 0.2) });
  c.push({ s: "thud", at: at + 1.15, g: 0.55, n: Math.round(n * 0.8), within: 0.8, pan: 0.35 });
}

/** Hand to hand: blades on blades, and on shields while there were shields. */
function melee(c: Cue[], r: Rng, at: number, dur: number, n: number, g: number, shields = true) {
  for (let k = 0; k < 3; k++) { // three knots of fighting across the field
    const p = span(r, -0.6, 0.6), share = Math.max(1, Math.round(n / 3));
    c.push({ s: "sword", at: at + k * 0.2, g, n: share, within: dur, pan: p, rate: span(r, 0.9, 1.1) });
    if (shields) c.push({ s: "shield", at: at + 0.1 + k * 0.2, g: g * 0.8, n: Math.max(1, Math.round(share * 0.45)), within: dur, pan: p });
  }
}

/** A burst of automatic fire. */
function burst(c: Cue[], s: SampleId, at: number, rate: number, n: number, g: number, pan = 0, far = 0) {
  c.push({ s, at, g, n, every: 1 / rate, pan, far });
}

/** Scattered rifle or musket fire along a line. */
function scattered(c: Cue[], r: Rng, s: SampleId, at: number, dur: number, n: number, g: number, far = 0) {
  for (let k = 0; k < 3; k++) c.push({ s, at: at + k * 0.15, g: g * span(r, 0.8, 1), n: Math.max(1, Math.round(n / 3)), within: dur, pan: span(r, -0.7, 0.7), far });
}

/** A shell screaming in, then bursting. */
function shell(c: Cue[], r: Rng, at: number, g: number, modern = false) {
  const len = modern ? span(r, 0.35, 0.5) : span(r, 0.8, 1.2), pan = span(r, -0.6, 0.6);
  c.push({ s: "whistle", at, g: g * 0.45, len, pan, rate: modern ? 1.5 : 1 });
  c.push({ s: "blast", at: at + len, g, pan, rate: span(r, 0.92, 1.08) });
}

function drumRoll(c: Cue[], at: number, dur: number) {
  c.push({ s: "snare", at, g: 0.42, n: Math.round(dur * 15), every: 1 / 15, pan: -0.2 }); // the long roll
  c.push({ s: "snare", at: at + dur + 0.12, g: 0.7, n: 4, every: 0.24, pan: -0.2 }); // then the beat
}

// --- the packages ---------------------------------------------------------------------------------------------

function blades(fx: number, m: number, r: Rng): Cue[] {
  const c: Cue[] = [];
  if (fx === FX_SIEGE) { // engines hurling stones at the walls, arrows from the walls, a ram at the gate
    c.push({ s: "treb", at: 0, g: 0.7, pan: -0.3 }, { s: "stone", at: 1.9, g: 0.9, pan: 0.25 });
    if (m >= 0.35) c.push({ s: "treb", at: 0.9, g: 0.55, pan: 0.35, far: 0.15 }, { s: "stone", at: 2.8, g: 0.8, pan: -0.2 });
    arrows(c, r, 0.4, m * 0.7);
    if (m >= 0.5) c.push({ s: "ram", at: 1.6, g: 0.7, n: 3, every: 1.3, pan: 0.1 });
    c.push({ s: "roar", at: 1.2, g: 0.35 + 0.2 * m, far: 0.2 });
  } else if (fx === FX_NAVAL) { // galleys rowing in, arrows, a ram striking, boarding
    c.push({ s: "oars", at: 0, g: 0.6, n: 2, every: 1.5 });
    arrows(c, r, 0.3, m * 0.6);
    c.push({ s: "splinter", at: 1.9, g: 0.9 }, { s: "splash", at: 2, g: 0.6, pan: 0.2 });
    c.push({ s: "roar", at: 1.6, g: 0.4 + 0.3 * m });
    melee(c, r, 2.4, 1.4, 4 + Math.round(8 * m), 0.7);
  } else if (fx === FX_UPRISING) {
    c.push({ s: "roar", at: 0, g: 0.5 });
    melee(c, r, 0.6, 2.2, 4 + Math.round(8 * m), 0.7);
  } else { // a battle: horns, arrows, the charge, the clash
    if (m >= 0.3) c.push({ s: "horn", at: 0, g: 0.75, pan: -0.35 });
    if (m >= 0.6) c.push({ s: "horn", at: 1.3, g: 0.6, pan: 0.45, far: 0.2, rate: 1.12 }); // the other side answers
    if (m >= 0.2) arrows(c, r, m >= 0.3 ? 0.6 : 0, m);
    if (m >= 0.45) c.push({ s: "hooves", at: 0.8, g: 0.45 + 0.4 * m });
    c.push({ s: "roar", at: 1.4, g: 0.45 + 0.4 * m });
    melee(c, r, 2, 2.2 + 1.5 * m, 6 + Math.round(18 * m), 0.8);
  }
  return c;
}

function powder(fx: number, t: number, m: number, r: Rng): Cue[] {
  const c: Cue[] = [];
  const pikes = clamp((1650 - t) / 230, 0.1, 0.7); // pike and shot: early on, much of it still hand to hand
  if (fx === FX_SIEGE) { // a bombardment: guns, their shot striking the walls; muskets from the walls
    const n = 3 + Math.round(4 * m);
    for (let k = 0; k < n; k++) {
      const at = 0.1 + k * Math.min(0.75, 3.2 / n) * span(r, 0.8, 1.1);
      c.push({ s: "cannon", at, g: 0.8, pan: span(r, -0.6, -0.2), far: 0.1 }, { s: "stone", at: at + span(r, 0.9, 1.2), g: 0.6, pan: span(r, 0.1, 0.6) });
    }
    scattered(c, r, "musket", 0.8, 3, 6 + Math.round(10 * m), 0.45);
    if (m >= 0.6) c.push({ s: "roar", at: 2.4, g: 0.3, far: 0.15 });
  } else if (fx === FX_NAVAL) { // a broadside rippling down a ship's side, and the answer
    if (t < 1650) c.push({ s: "oars", at: 0, g: 0.5 }); // galleys still fought (Lepanto, 1571)
    const n = 6 + Math.round(12 * m);
    c.push({ s: "cannon", at: 0.2, g: 0.75, n, every: span(r, 0.13, 0.19), pan: -0.35 });
    c.push({ s: "cannon", at: 2.4, g: 0.55, n: Math.round(n * 0.7), every: span(r, 0.15, 0.23), pan: 0.55, far: 0.3 });
    c.push({ s: "splinter", at: 1, g: 0.8, pan: 0.2 }, { s: "splinter", at: 2.9, g: 0.7, pan: -0.3 });
    c.push({ s: "splash", at: 0.6, g: 0.5, n: 2 + Math.round(3 * m), within: 2.9, pan: span(r, -0.5, 0.5) });
    scattered(c, r, "musket", 1, 2.5, 4 + Math.round(6 * m), 0.35);
  } else if (fx === FX_UPRISING) {
    c.push({ s: "roar", at: 0, g: 0.45 });
    scattered(c, r, "musket", 0.3, 2.9, 6 + Math.round(8 * m), 0.5);
    melee(c, r, 1, 2, 3 + Math.round(4 * m), 0.55, t < 1700);
  } else { // drums beat; volleys roll along the lines; the guns; cavalry; pikes and bayonets
    if (t >= 1500 && m >= 0.3) drumRoll(c, 0, 1 + 0.5 * m);
    if (t >= 1750 && m >= 0.5) c.push({ s: "bugle", at: 0.9, g: 0.5, pan: 0.3, far: 0.15 });
    const volleys = 1 + Math.round(2 * m);
    for (let v = 0; v < volleys; v++) {
      c.push({ s: "musket", at: 0.6 + v * span(r, 1.1, 1.5), g: 0.6, n: 10 + Math.round(18 * m), within: 0.45, pan: v % 2 ? 0.35 : -0.35 });
    }
    scattered(c, r, "musket", 0.8, 3, 4 + Math.round(8 * m), 0.4);
    const guns = 1 + Math.round(3 * m);
    for (let k = 0; k < guns; k++) c.push({ s: "cannon", at: span(r, 0.3, 3.5), g: 0.8, pan: span(r, -0.7, 0.7), far: span(r, 0, 0.2) });
    if (m >= 0.5) c.push({ s: "hooves", at: 1.4, g: 0.35 + 0.3 * m });
    if (pikes > 0.25 || m >= 0.6) {
      c.push({ s: "roar", at: 1.8, g: 0.3 + 0.2 * pikes });
      melee(c, r, 2.2, 1.6, Math.round((4 + 10 * m) * (pikes + 0.3)), 0.6, t < 1700);
    }
  }
  return c;
}

function rifles(fx: number, t: number, m: number, r: Rng): Cue[] {
  const c: Cue[] = [];
  const maxim = t >= 1884, gatling = t >= 1862;
  if (fx === FX_SIEGE) { // guns and shells; rifle fire from the trenches
    c.push({ s: "cannon", at: 0.2, g: 0.6, n: 3 + Math.round(3 * m), every: span(r, 0.5, 0.8), far: 0.3, pan: -0.4 });
    for (let k = 0; k < 2 + Math.round(3 * m); k++) shell(c, r, 0.5 + k * span(r, 0.6, 0.9), 0.75);
    scattered(c, r, "rifle", 0.5, 3, 8 + Math.round(12 * m), 0.45);
    if (maxim) burst(c, "mg", 1.6, 10, 12 + Math.round(10 * m), 0.5, span(r, -0.5, 0.5));
  } else if (fx === FX_NAVAL) { // ironclads and dreadnoughts: few, huge guns; great columns of water
    const guns = 2 + Math.round(3 * m);
    for (let k = 0; k < guns; k++) {
      const at = 0.2 + k * Math.min(1, 2.8 / guns) * span(r, 0.8, 1.1);
      c.push({ s: "cannon", at, g: 0.9, rate: 0.8, pan: -0.4 }, { s: "splash", at: at + 1.6, g: 0.75, rate: 0.8, pan: span(r, 0, 0.6) });
    }
    c.push({ s: "blast", at: span(r, 2.4, 3.4), g: 0.85, n: 1 + Math.round(m), every: 0.7, pan: 0.3 });
  } else if (fx === FX_UPRISING) {
    c.push({ s: "roar", at: 0, g: 0.4 });
    scattered(c, r, "rifle", 0.3, 2.9, 8 + Math.round(10 * m), 0.5);
    if (maxim && m >= 0.5) burst(c, "mg", 1.5, 10, 14, 0.5, span(r, -0.5, 0.5));
  } else {
    if (t < 1912 && m >= 0.4) c.push({ s: "bugle", at: 0, g: 0.5, far: 0.1, pan: -0.3 });
    scattered(c, r, "rifle", 0.4, 3.4, 12 + Math.round(26 * m), 0.5);
    if (t < 1890 && m >= 0.3) c.push({ s: "rifle", at: 1, g: 0.55, n: 10, within: 0.3, pan: 0.3 }); // a volley, still
    c.push({ s: "cannon", at: span(r, 0.2, 1), g: 0.7, n: 1 + Math.round(2 * m), every: span(r, 0.9, 1.4), far: 0.25, pan: span(r, -0.6, 0.6) });
    for (let k = 0; k < 1 + Math.round(2 * m); k++) shell(c, r, 0.8 + k * span(r, 1, 1.4), 0.7);
    if (maxim) {
      for (let b = 0; b < 1 + Math.round(2 * m); b++) burst(c, "mg", 1 + b * span(r, 1.1, 1.5), span(r, 9.5, 10.5), 10 + Math.round(span(r, 0, 14)), 0.55, span(r, -0.6, 0.6));
    } else if (gatling && m >= 0.5) burst(c, "mg", 1.2, span(r, 6, 7), 8 + Math.round(span(r, 0, 6)), 0.5, span(r, -0.5, 0.5));
    if (m >= 0.5 && t < 1905) {
      c.push({ s: "hooves", at: 1.2, g: 0.35 });
      melee(c, r, 2.4, 1.2, 4, 0.5, false); // sabres
    }
    if (m >= 0.6) c.push({ s: "roar", at: 2, g: 0.25 });
  }
  return c;
}

function worldWars(fx: number, t: number, m: number, r: Rng): Cue[] {
  const c: Cue[] = [];
  const mgRate = () => (t >= 1939 && r() < 0.5 ? span(r, 19, 21) : span(r, 8.5, 11)); // MG 42 tore; Maxims and Brownings rattled
  if (fx === FX_BOMBING) { // the sirens, the bombers, the flak, a stick of bombs walking across the town
    c.push({ s: "siren", at: 0, g: 0.5, len: 7, far: 0.35, pan: -0.2 });
    if (t >= 1937 && t < 1946 && r() < 0.4) {
      c.push({ s: "stuka", at: 0.8, g: 0.6, len: 3, pan: 0.2 });
      shell(c, r, 3.8, 1);
    } else {
      c.push({ s: "planes", at: 0.4, g: 0.55, len: 7, sweep: 0.8, far: 0.1 });
      const n = 5 + Math.round(5 * m), gap = Math.min(0.28, 2.2 / n);
      for (let k = 0; k < n; k++) {
        c.push({ s: "whistle", at: 2.2 + k * gap, g: 0.3, len: 1.3, pan: -0.6 + (1.2 * k) / n });
        c.push({ s: "blast", at: 3.5 + k * gap, g: 0.85, pan: -0.6 + (1.2 * k) / n, rate: span(r, 0.9, 1.1) });
      }
    }
    c.push({ s: "blast", at: 1, g: 0.25, n: 3 + Math.round(4 * m), within: 4, rate: 1.6, far: 0.5, pan: span(r, -0.6, 0.6) }); // flak, high up
  } else if (fx === FX_NAVAL) {
    const guns = 2 + Math.round(3 * m);
    for (let k = 0; k < guns; k++) {
      const at = 0.2 + k * Math.min(0.9, 2.8 / guns) * span(r, 0.8, 1.05);
      c.push({ s: "cannon", at, g: 0.9, rate: 0.75, pan: -0.4 }, { s: "splash", at: at + 1.4, g: 0.8, rate: 0.75, pan: span(r, 0, 0.6) });
    }
    if (t >= 1939 && m >= 0.4) { // carriers: aircraft overhead, the anti-aircraft guns, a bomb or a torpedo
      c.push({ s: "planes", at: 0.3, g: 0.45, len: 5, sweep: 0.9 });
      burst(c, "hmg", 0.8, 6, 14, 0.4, 0.3, 0.1);
      shell(c, r, 2.2, 0.95);
    }
    c.push({ s: "blast", at: span(r, 2.4, 3.6), g: 0.9, n: 1 + Math.round(m), every: 0.8, pan: 0.2 });
  } else if (fx === FX_UPRISING) {
    scattered(c, r, "rifle", 0.2, 3, 8 + Math.round(10 * m), 0.5);
    for (let b = 0; b < 1 + Math.round(m); b++) burst(c, "mg", 0.6 + b * 1.4, mgRate(), 10 + Math.round(10 * m), 0.5, span(r, -0.6, 0.6));
    c.push({ s: "blast", at: span(r, 1, 2.5), g: 0.4, n: 1 + Math.round(m), every: 1.1, rate: 1.5 }); // grenades
    c.push({ s: "roar", at: 0.5, g: 0.25, far: 0.2 });
  } else { // a front: shells screaming in, the guns far behind; machine guns and rifles along the line
    const heavy = fx === FX_SIEGE ? 2 : 0, shells = 2 + heavy + Math.round(4 * m);
    for (let k = 0; k < shells; k++) shell(c, r, 0.2 + k * Math.min(0.85, 3.6 / shells) * span(r, 0.75, 1.05), 0.85 + 0.15 * m);
    c.push({ s: "cannon", at: span(r, 0, 0.8), g: 0.5, n: 2 + heavy + Math.round(3 * m), within: 3, rate: 0.9, far: 0.45, pan: span(r, -0.6, 0.6) });
    for (let b = 0; b < 2 + Math.round(3 * m); b++) {
      const rate = mgRate();
      burst(c, "mg", 0.4 + b * span(r, 0.6, 1), rate, Math.round(rate * span(r, 0.6, 1.5)), 0.55, side(r, b % 2 ? 1 : -1));
    }
    scattered(c, r, "rifle", 0.2, 3.6, 6 + Math.round(14 * m), 0.45);
    if (t >= 1915) c.push({ s: "blast", at: span(r, 1.5, 3), g: 0.35, n: 1 + Math.round(m), every: 0.9, rate: 1.5, pan: span(r, -0.5, 0.5) }); // grenades
    if ((t >= 1939 && m >= 0.45) || (t >= 1916 && m >= 0.75)) { // armour
      c.push({ s: "tank", at: 0.3, g: 0.45, len: 3.5, pan: span(r, -0.5, 0.5) }, { s: "cannon", at: 1.6, g: 0.7, rate: 1.3, pan: span(r, -0.5, 0.5) });
    }
    if (t >= 1936 && m >= 0.55) { // fighters strafing
      c.push({ s: "planes", at: 0.6, g: 0.4, len: 4, sweep: 1, rate: 1.4 });
      burst(c, "mg", 2, 20, 16, 0.45, 0, 0.2);
    }
    if (t >= 1941.5 && t < 1946 && m >= 0.7 && r() < 0.35) { // a Katyusha salvo
      c.push({ s: "rocket", at: 0.3, g: 0.35, n: 12, every: 0.14, far: 0.4, pan: -0.5 });
      c.push({ s: "blast", at: 3, g: 0.6, n: 6, every: 0.12, far: 0.3, pan: 0.4 });
    }
  }
  return c;
}

function modern(fx: number, t: number, m: number, r: Rng): Cue[] {
  const c: Cue[] = [];
  const drones = t >= 2014 && r() < (t >= 2022 ? 0.55 : 0.25);
  if (fx === FX_BOMBING) { // sirens, jets, missiles coming in, anti-aircraft fire, drones
    c.push({ s: "siren", at: 0, g: 0.45, len: 7, far: 0.35, pan: 0.2 });
    c.push({ s: "jet", at: 0.6, g: 0.55, len: 4, sweep: 1 });
    for (let k = 0; k < 2 + Math.round(3 * m); k++) {
      const at = 1.5 + k * 0.6, p = span(r, -0.6, 0.6);
      c.push({ s: "rocket", at, g: 0.4, far: 0.2, pan: p }, { s: "blast", at: at + 1.2, g: 0.95, pan: p });
    }
    burst(c, "hmg", 1, 14, 20, 0.3, -0.4, 0.4);
    if (drones) c.push({ s: "drone", at: 0.2, g: 0.4, len: 4, pan: 0.5 });
  } else if (fx === FX_NAVAL) { // an anti-ship missile, the guns, the close-in weapons' roar
    c.push({ s: "rocket", at: 0.2, g: 0.7, pan: -0.4 }, { s: "blast", at: 2, g: 1, pan: 0.3 });
    c.push({ s: "cannon", at: 0.4, g: 0.7, rate: 1.2, n: 4, every: 0.9, pan: -0.3 });
    burst(c, "ar", 2.5, 50, 40, 0.35, 0.2);
    if (t >= 1962 && r() < 0.4) c.push({ s: "rotor", at: 0, g: 0.4, len: 4.5, sweep: 1 });
  } else if (fx === FX_SIEGE) { // shelling, snipers, bursts from the ruins
    for (let k = 0; k < 3 + Math.round(4 * m); k++) shell(c, r, 0.2 + k * span(r, 0.4, 0.8), 0.9, true);
    c.push({ s: "rifle", at: span(r, 0.5, 1.5), g: 0.6, n: 3, every: span(r, 0.9, 1.3), rate: 0.9, pan: span(r, -0.6, 0.6), far: 0.1 });
    for (let b = 0; b < 2 + Math.round(2 * m); b++) burst(c, "ar", 0.6 + b * span(r, 0.7, 1.1), span(r, 10, 12.5), 3 + Math.round(span(r, 0, 6)), 0.5, side(r, b % 2 ? 1 : -1));
    burst(c, "hmg", 2, 8.5, 8, 0.5, span(r, -0.5, 0.5));
    c.push({ s: "cannon", at: 0.5, g: 0.5, rate: 1.6, n: 2, every: 1.4, far: 0.3 }); // mortars
  } else if (fx === FX_UPRISING) {
    c.push({ s: "roar", at: 0, g: 0.45 });
    for (let b = 0; b < 2 + Math.round(3 * m); b++) burst(c, "ar", 0.5 + b * span(r, 0.6, 0.9), span(r, 10, 12.5), 3 + Math.round(span(r, 0, 5)), 0.5, side(r, b % 2 ? 1 : -1));
    scattered(c, r, "ar", 0.3, 3, 4 + Math.round(6 * m), 0.4);
    c.push({ s: "blast", at: span(r, 1.5, 3), g: 0.35, rate: 0.8 });
  } else { // a firefight: bursts from both sides, heavy machine guns, rockets and shells; helicopters, jets, drones
    const bursts = 3 + Math.round(6 * m);
    for (let b = 0; b < bursts; b++) {
      burst(c, "ar", 0.1 + b * span(r, 0.3, 0.55), span(r, 10, 12.5), 3 + Math.floor(span(r, 0, 7)), 0.55, side(r, b % 2 ? 1 : -1));
    }
    scattered(c, r, "ar", 0.2, 3.6, 6 + Math.round(10 * m), 0.45);
    for (let b = 0; b < 1 + Math.round(2 * m); b++) burst(c, "hmg", 0.6 + b * span(r, 0.9, 1.4), 8.5, 5 + Math.round(span(r, 0, 7)), 0.6, span(r, -0.6, 0.6));
    for (let k = 0; k < 1 + Math.round(3 * m); k++) {
      if (r() < 0.5) shell(c, r, 0.5 + k * span(r, 0.6, 1), 0.85, true);
      else c.push({ s: "blast", at: 0.5 + k * span(r, 0.6, 1), g: 0.85, pan: span(r, -0.6, 0.6) });
    }
    if (m >= 0.3) { // a rocket-propelled grenade
      const at = span(r, 0.8, 2.8), p = span(r, -0.5, 0.5);
      c.push({ s: "rocket", at, g: 0.5, pan: p }, { s: "blast", at: at + 0.9, g: 0.8, pan: clamp(p + 0.3, -1, 1) });
    }
    if (t >= 1962 && m >= 0.5 && r() < 0.5) {
      c.push({ s: "rotor", at: 0, g: 0.45, len: 4.5, sweep: 1 });
      if (t >= 1966) burst(c, "ar", 2, 50, 45, 0.35, 0, 0.15); // a door gun
    }
    if (t >= 1965 && m >= 0.7 && r() < 0.4) c.push({ s: "jet", at: 0.5, g: 0.6, len: 3.5, sweep: 1 }, { s: "blast", at: 2.6, g: 1 });
    if (drones) {
      const p = span(r, -0.5, 0.5);
      c.push({ s: "drone", at: 0.2, g: 0.4, len: 2.8, pan: p }, { s: "blast", at: 3, g: 0.6, rate: 1.4, pan: p });
    }
    if (m >= 0.65 && r() < 0.35) { // rocket artillery
      c.push({ s: "rocket", at: 0.2, g: 0.25, n: 12, every: 0.12, far: 0.5, pan: -0.5 });
      c.push({ s: "blast", at: 3, g: 0.55, n: 8, every: 0.15, far: 0.3, pan: 0.4 });
    }
  }
  return c;
}

/** The sounds of event of effect fx (logic.FX_*) at time t, of magnitude m (0–1). The atomic bombings are scored in
 * the engine (their silence, then the rumble); atrocities get a bell or nothing. */
export function scoreEvent(fx: number, t: number, m: number, r: Rng): Cue[] {
  if (fx === FX_NONE || fx === FX_NUKE) return [];
  if (fx === FX_MEMORIAL) return m >= 0.3 ? [{ s: "bell", at: 0, g: m }] : [];
  const era = eraOf(t);
  const c = era === ERA_BLADE ? blades(fx, m, r) : era === ERA_POWDER ? powder(fx, t, m, r) : era === ERA_RIFLE ? rifles(fx, t, m, r)
    : era === ERA_SHELL ? worldWars(fx, t, m, r) : modern(fx, t, m, r);
  if (era >= ERA_POWDER && m >= 0.6 && fx !== FX_UPRISING && fx !== FX_BOMBING) c.push({ s: "boom", at: 0, g: m }); // the ground shakes
  return c.sort((a, b) => a.at - b.at);
}

/** A big battle off screen: heard far away. */
export function scoreDistant(t: number, m: number, r: Rng): Cue[] {
  const era = eraOf(t), far = 0.85;
  if (era === ERA_BLADE) return [r() < 0.5 ? { s: "horn", at: 0, g: 0.5 * m + 0.2, far } : { s: "roar", at: 0, g: 0.4 * m + 0.2, far }];
  if (era <= ERA_RIFLE) return [{ s: "cannon", at: 0, g: 0.6 * m + 0.2, n: r() < 0.5 ? 1 : 2, every: 0.7, far }];
  if (era === ERA_SHELL) return [r() < 0.6 ? { s: "blast", at: 0, g: 0.6 * m + 0.2, far } : { s: "mg", at: 0, g: 0.5, n: 14, every: 0.1, far }];
  return [r() < 0.6 ? { s: "blast", at: 0, g: 0.6 * m + 0.2, far } : { s: "ar", at: 0, g: 0.5, n: 8, every: 0.09, far }];
}

/** The fighting around the places burning on screen: one small piece of a period's battle (a burst of fire, a
 * shell landing, a knot of men clashing), heard a little further off than the events themselves. */
export function scoreAmbient(t: number, r: Rng): Cue[] {
  const era = eraOf(t), x = r(), far = 0.15, p = span(r, -0.6, 0.6);
  if (era === ERA_BLADE) {
    if (x < 0.4) return [{ s: "sword", at: 0, g: 0.45, n: 2 + Math.floor(4 * r()), within: 1.2, pan: p, far }, { s: "shield", at: 0.2, g: 0.35, n: 2, within: 1, pan: p, far }];
    if (x < 0.55) return [{ s: "roar", at: 0, g: 0.3, pan: p, far }];
    if (x < 0.7) { const c: Cue[] = []; arrows(c, r, 0, 0.25); return c.map((q) => ({ ...q, g: q.g * 0.6, far })); }
    if (x < 0.78 && t < 1600) return [{ s: "horn", at: 0, g: 0.3, far: 0.6, pan: p }];
    if (x < 0.85) return [{ s: "hooves", at: 0, g: 0.25, far: 0.3, pan: p }];
    return [{ s: "wardrum", at: 0, g: 0.35, n: 3, every: 0.45, far: 0.4, pan: p }];
  }
  if (era === ERA_POWDER) {
    if (x < 0.4) return [{ s: "musket", at: 0, g: 0.4, n: 4 + Math.floor(6 * r()), within: 1.5, pan: p, far }];
    if (x < 0.55) return [{ s: "musket", at: 0, g: 0.45, n: 10, within: 0.4, pan: p, far }];
    if (x < 0.8) return [{ s: "cannon", at: 0, g: 0.5, pan: p, far: 0.3 }];
    if (x < 0.88 && t >= 1500) return [{ s: "snare", at: 0, g: 0.35, n: 12, every: 1 / 15, far: 0.4, pan: p }];
    if (x < 0.95) return [{ s: "sword", at: 0, g: 0.35, n: 3, within: 1, pan: p, far }];
    return [{ s: "hooves", at: 0, g: 0.22, far: 0.35, pan: p }];
  }
  if (era === ERA_RIFLE) {
    if (x < 0.4) return [{ s: "rifle", at: 0, g: 0.4, n: 3 + Math.floor(6 * r()), within: 1.6, pan: p, far }];
    if (x < 0.6 && t >= 1884) return [{ s: "mg", at: 0, g: 0.4, n: 8 + Math.floor(10 * r()), every: 0.1, pan: p, far }];
    if (x < 0.85) { const c: Cue[] = []; shell(c, r, 0, 0.55); return c.map((q) => ({ ...q, far: 0.25 })); }
    if (x < 0.9 && t < 1912) return [{ s: "bugle", at: 0, g: 0.25, far: 0.5, pan: p }];
    return [{ s: "cannon", at: 0, g: 0.45, far: 0.4, pan: p }];
  }
  if (era === ERA_SHELL) {
    if (x < 0.35) { const rate = t >= 1939 && r() < 0.5 ? 20 : 10; return [{ s: "mg", at: 0, g: 0.45, n: Math.round(rate * span(r, 0.6, 1.6)), every: 1 / rate, pan: p, far }]; }
    if (x < 0.6) return [{ s: "rifle", at: 0, g: 0.4, n: 2 + Math.floor(5 * r()), within: 1.5, pan: p, far }];
    if (x < 0.8) { const c: Cue[] = []; shell(c, r, 0, 0.6); return c.map((q) => ({ ...q, far: 0.25 })); }
    if (x < 0.95) return [{ s: "cannon", at: 0, g: 0.4, n: 2, every: 0.8, rate: 0.9, far: 0.5, pan: p }];
    if (t >= 1939) return [{ s: "tank", at: 0, g: 0.3, len: 3, far: 0.3, pan: p }];
    return [{ s: "blast", at: 0, g: 0.4, far: 0.5, pan: p }];
  }
  if (x < 0.4) return [0, 1, 2].map((k) => ({ s: "ar" as const, at: k * span(r, 0.4, 0.8), g: 0.42, n: 3 + Math.floor(5 * r()), every: 1 / span(r, 10, 12.5), pan: side(r, k % 2 ? 1 : -1), far }));
  if (x < 0.52) return [{ s: "hmg", at: 0, g: 0.45, n: 5 + Math.floor(6 * r()), every: 1 / 8.5, pan: p, far }];
  if (x < 0.72) return [{ s: "blast", at: 0, g: 0.55, far: 0.25, pan: p }];
  if (x < 0.8) return [{ s: "rocket", at: 0, g: 0.35, pan: p, far }, { s: "blast", at: 0.9, g: 0.6, pan: p, far }];
  if (x < 0.88) return [{ s: "ar", at: 0, g: 0.35, n: 6 + Math.floor(6 * r()), within: 1.5, pan: p, far }];
  if (x < 0.94 && t >= 2014) return [{ s: "drone", at: 0, g: 0.3, len: 2.5, far: 0.2, pan: p }, { s: "blast", at: 2.6, g: 0.45, rate: 1.4, far: 0.2, pan: p }];
  if (t >= 1962) return [{ s: "rotor", at: 0, g: 0.3, len: 4, sweep: 0.8, far: 0.3 }];
  return [{ s: "blast", at: 0, g: 0.5, far: 0.3, pan: p }];
}

/** When each repeat of a cue starts, after the cue: a burst keeps its beat (each gap a little uneven, the rounds never
 * drifting off it), a volley comes at random within its window. */
export function repeatTimes(c: Cue, r: Rng): number[] {
  const n = c.n ?? 1, out: number[] = [];
  for (let k = 0; k < n; k++) {
    out.push(c.every ? Math.max(0, k * c.every + (r() - 0.5) * 0.1 * c.every) : c.within ? r() * c.within : 0);
  }
  return out;
}

// moments across each period, so that preparing a period prepares the sounds of all of it (the Maxim from 1884,
// tanks from 1916, helicopters from 1962…)
const ACROSS = [[-500, 800, 1300, 1419], [1420, 1500, 1650, 1760, 1859], [1860, 1870, 1886, 1911], [1912, 1917, 1938, 1942, 1959],
  [1960, 1966, 1990, 2015, 2023]];

/** The rendered samples a period may need (to prepare them ahead), commonest first. */
export function samplesFor(t: number): SampleId[] {
  const count = new Map<SampleId, number>();
  let seed = 1;
  const r: Rng = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  const add = (cs: Cue[]) => { for (const q of cs) if (!LIVE.has(q.s)) count.set(q.s as SampleId, (count.get(q.s as SampleId) ?? 0) + 1); };
  for (const y of ACROSS[eraOf(t)]) {
    for (const fx of [FX_BATTLE, FX_SIEGE, FX_NAVAL, FX_BOMBING, FX_UPRISING]) {
      for (const m of [0.15, 0.5, 0.95]) for (let k = 0; k < 6; k++) add(scoreEvent(fx, y, m, r));
    }
    for (let k = 0; k < 40; k++) add([...scoreAmbient(y, r), ...scoreDistant(y, 0.8, r)]);
  }
  return [...count.keys()].sort((a, b) => count.get(b)! - count.get(a)!);
}
